/**
 * Dashboard "Active bookings" (Dashboard idea 7): the stock reserved for projects (GET /reservations),
 * with the needed-by date (red once it has passed) and Issue DN / Release on each booking. Empty, it
 * explains what a booking is for.
 */
import { ArrowRight, ArrowRightLeft, CalendarDays, CloudOff, Lock, Truck, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, formatNumber, formatRelativeDays } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CardFoot, CardHead, CountPill, InvCard, LinkButton } from './InventoryUi';
import { bookingLateDays, formatQty, locationShort } from './inventoryModel';

const PREVIEW = 6;

export default function ActiveBookings({
  reservations = [],
  loading = false,
  error = '',
  onRetry,
  onReserve,
  onIssue,
  onRelease,
  onShowAll,
  canEdit = true,
  className
}) {
  const sorted = [...reservations].sort((left, right) => (
    String(left.needed_by_date || '9999').localeCompare(String(right.needed_by_date || '9999'))
    || String(left.project_name || '').localeCompare(String(right.project_name || ''))
  ));
  const shown = sorted.slice(0, PREVIEW);
  const lateCount = sorted.filter((reservation) => (bookingLateDays(reservation) ?? -1) > 0).length;

  return (
    <InvCard className={cn('flex flex-col', className)} data-testid="inventory-active-bookings">
      <CardHead
        icon={Lock}
        title="Active bookings"
        badge={<CountPill tone={lateCount ? 'red' : reservations.length ? 'blue' : 'gray'}>{formatNumber(reservations.length)}</CountPill>}
        right={canEdit && reservations.length ? (
          <Button type="button" size="xs" variant="outline" className="text-ink" onClick={() => onReserve?.()}>
            <Lock aria-hidden="true" /> Reserve
          </Button>
        ) : null}
      />
      {error ? (
        <EmptyState
          layout="inline"
          tone="red"
          icon={CloudOff}
          title="Couldn't load the bookings"
          text={error}
          action={<Button type="button" size="sm" variant="outline" className="text-ink" onClick={onRetry}>Try again</Button>}
        />
      ) : loading && !reservations.length ? (
        <p className="px-5 py-6 text-[13px] text-ink-3">Loading bookings…</p>
      ) : shown.length ? (
        <ul className="divide-y divide-line-2" aria-label="Active bookings">
          {shown.map((reservation) => {
            const late = bookingLateDays(reservation);
            const isLate = late !== null && late > 0;
            return (
              <li key={reservation.id} className="px-5 py-3" data-testid={`booking-${reservation.id}`}>
                <div className="flex min-w-0 items-center gap-2 text-[13px]">
                  <span className="truncate font-mono text-[12.5px] text-ink" title={reservation.description}>{reservation.part_number}</span>
                  <b className="shrink-0 font-bold tabular-nums">{formatQty(reservation.qty_reserved)} pcs</b>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
                  <span className="min-w-0 truncate font-semibold text-ink" title={reservation.project_name}>{reservation.project_name || 'Unknown project'}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className={cn('inline-flex items-center gap-1 text-[12px]', isLate ? 'font-semibold text-status-red-ink' : 'text-ink-3')}>
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                    {reservation.needed_by_date
                      ? <>Needed by {formatDate(reservation.needed_by_date, { year: 'auto' })} · {formatRelativeDays(reservation.needed_by_date, { style: 'due' })}</>
                      : 'No needed-by date'}
                  </span>
                  <span className="text-[12px] text-ink-3">{locationShort(reservation.location)}</span>
                  {canEdit ? (
                  <span className="ml-auto flex items-center gap-1.5">
                    <Button type="button" size="xs" variant="outline" className="text-ink" onClick={() => onIssue?.(reservation)} data-testid={`booking-issue-${reservation.id}`}>
                      <Truck aria-hidden="true" /> Issue DN
                    </Button>
                    <Button type="button" size="xs" variant="ghost" className="text-ink-2" onClick={() => onRelease?.(reservation)} data-testid={`booking-release-${reservation.id}`}>
                      <Unlock aria-hidden="true" /> Release
                    </Button>
                  </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center px-6 pb-6 pt-7 text-center" data-testid="bookings-empty">
          <span className="ui-icon-tile tone-blue h-12 w-12 rounded-2xl" aria-hidden="true"><Lock className="h-[22px] w-[22px]" /></span>
          <h4 className="mt-4 font-heading text-[15px] font-bold text-ink">No stock is reserved for a project</h4>
          <p className="mt-1.5 max-w-[300px] text-[13px] leading-relaxed text-ink-3">
            Reserve items for a site so nobody transfers or issues them elsewhere. A booking looks like this:
          </p>
          <div className="relative mt-4 w-full max-w-[300px] rounded-xl border border-dashed border-[color:var(--line-dashed)] px-3.5 pb-3 pt-4 text-left" aria-hidden="true">
            <span className="absolute -top-2 left-3 bg-surface px-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-3">Example</span>
            <div className="flex items-center gap-2 text-[12.5px] text-ink-2">
              <span className="font-mono">MTN693003</span><b className="text-ink">5 pcs</b>
              <ArrowRightLeft className="h-3 w-3 text-ink-4" /><span>LIV Waterside</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11.5px] text-ink-3">
              <CalendarDays className="h-3 w-3" /> Needed by 12 Oct
              <span className="ml-auto rounded-md border border-line px-1.5 py-0.5">Issue DN</span>
              <span className="rounded-md border border-line px-1.5 py-0.5">Release</span>
            </div>
          </div>
          {canEdit ? (
            <Button type="button" size="sm" variant="outline" className="mt-4 text-ink" onClick={() => onReserve?.()} data-testid="bookings-reserve">
              <Lock aria-hidden="true" /> Reserve stock
            </Button>
          ) : null}
        </div>
      )}
      {sorted.length > PREVIEW || lateCount ? (
        <CardFoot className="mt-auto justify-between">
          <span>{lateCount ? <b className="font-semibold text-status-red-ink">{formatNumber(lateCount)} past the needed-by date</b> : `${formatNumber(sorted.length)} bookings`}</span>
          {sorted.length > PREVIEW ? <LinkButton onClick={onShowAll}>All {formatNumber(sorted.length)} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton> : null}
        </CardFoot>
      ) : null}
    </InvCard>
  );
}
